// YouTube API utilities
const YOUTUBE_API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;
const CORS_PROXY = 'https://api.allorigins.win/raw?url=';

// Extract channel ID from URL
export const extractChannelId = (url) => {
  try {
    // Handle different YouTube URL formats
    const patterns = [
      // Channel ID: /channel/UCxxxxxx
      /(?:youtube\.com\/channel\/)([a-zA-Z0-9_-]+)/,
      // Custom URL: /c/channelname or /@handle
      /(?:youtube\.com\/c\/)([a-zA-Z0-9_-]+)/,
      /(?:youtube\.com\/@)([a-zA-Z0-9_-]+)/,
      // User: /user/username
      /(?:youtube\.com\/user\/)([a-zA-Z0-9_-]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return { id: match[1], type: getUrlType(pattern) };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting channel ID:', error);
    return null;
  }
};

const getUrlType = (pattern) => {
  if (pattern.source.includes('channel')) return 'channel';
  if (pattern.source.includes('@')) return 'handle';
  if (pattern.source.includes('/c/')) return 'custom';
  if (pattern.source.includes('user')) return 'user';
  return 'unknown';
};

// Fetch channel info using YouTube Data API v3
export const fetchChannelInfo = async (url) => {
  try {
    const extracted = extractChannelId(url);
    if (!extracted) {
      throw new Error('Invalid YouTube URL format');
    }

    // Try to get channel info without API key first (using a public scraper approach)
    const channelInfo = await fetchChannelInfoNoAPI(url);
    return channelInfo;
    
  } catch (error) {
    console.error('Error fetching channel info:', error);
    
    // Fallback to simulated data
    const extracted = extractChannelId(url);
    const channelName = extracted ? `Canal ${extracted.id}` : 'Canal do YouTube';
    
    return {
      name: channelName,
      thumbnailUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(channelName)}&background=dc2626&color=ffffff&size=128`,
      subscriberCount: null,
      description: null
    };
  }
};

// Alternative method without API key - scraping approach
const fetchChannelInfoNoAPI = async (url) => {
  try {
    // Use a CORS proxy to fetch the YouTube page
    const response = await fetch(`${CORS_PROXY}${encodeURIComponent(url)}`);
    const html = await response.text();
    
    // Extract channel name from page title or meta tags
    const titleMatch = html.match(/<title>([^<]+)<\/title>/i);
    const title = titleMatch ? titleMatch[1].replace(' - YouTube', '') : 'Canal do YouTube';
    
    // Try to extract channel thumbnail
    const thumbnailMatch = html.match(/"avatar":{"thumbnails":\[{"url":"([^"]+)"/);
    const thumbnailUrl = thumbnailMatch ? thumbnailMatch[1] : 
      `https://ui-avatars.com/api/?name=${encodeURIComponent(title)}&background=dc2626&color=ffffff&size=128`;
    
    // Extract subscriber count if available
    const subscriberMatch = html.match(/(\d+(?:\.\d+)?[KMB]?) subscriber/i);
    const subscriberCount = subscriberMatch ? subscriberMatch[1] : null;
    
    return {
      name: title,
      thumbnailUrl: thumbnailUrl,
      subscriberCount: subscriberCount,
      description: null
    };
    
  } catch (error) {
    console.error('Error in no-API fetch:', error);
    throw error;
  }
};

// Validate YouTube URL
export const isValidYouTubeUrl = (url) => {
  const patterns = [
    /^https?:\/\/(www\.)?youtube\.com\/channel\/[a-zA-Z0-9_-]+/,
    /^https?:\/\/(www\.)?youtube\.com\/c\/[a-zA-Z0-9_-]+/,
    /^https?:\/\/(www\.)?youtube\.com\/@[a-zA-Z0-9_-]+/,
    /^https?:\/\/(www\.)?youtube\.com\/user\/[a-zA-Z0-9_-]+/
  ];
  
  return patterns.some(pattern => pattern.test(url));
};